import type { SubAccount } from '@talos/kyoko';
import { useMemo } from 'react';
import { useSubAccounts } from '../providers/SubAccountsContext';
import { useTradingSettings } from '../providers/TradingSettingsContext';

interface UseDefaultSubAccount {
  defaultSubAccount: SubAccount | undefined;
  defaultClickToTradeSubAccount: SubAccount | undefined;
}

/**
 * Get the default subaccount.
 * @param currentTabDefaultAccountId pass in the default subaccount from the current tab if it exists, to override any global settings.
 */
export function useDefaultSubAccount(currentTabDefaultAccountId?: number): UseDefaultSubAccount {
  const { subAccountsByID, subAccountsByName, tradableSubAccountNamesSet } = useSubAccounts();
  const { defaultSubAccountId: globalDefaultSubAccountId, clickToTradeDefaultSubaccount } = useTradingSettings();

  const defaultSubAccount = useMemo(() => {
    const subAccountID = currentTabDefaultAccountId ?? globalDefaultSubAccountId;
    return getSubAccount(subAccountID, subAccountsByID, tradableSubAccountNamesSet);
  }, [globalDefaultSubAccountId, subAccountsByID, tradableSubAccountNamesSet, currentTabDefaultAccountId]);

  // Order:
  // 1) tab specific default SubAccount,
  // 2) global default SubAccount for Click to trade,
  // 3) global default SubAccount (latest used)
  const defaultClickToTradeSubAccount = useMemo(() => {
    const clickToTradeDefaultSubAccountId = clickToTradeDefaultSubaccount
      ? subAccountsByName.get(clickToTradeDefaultSubaccount)?.SubaccountID
      : undefined;

    let result: SubAccount | undefined = undefined;
    for (const subAccountID of [
      currentTabDefaultAccountId,
      clickToTradeDefaultSubAccountId,
      globalDefaultSubAccountId,
    ]) {
      const subAccount = getSubAccount(subAccountID, subAccountsByID, tradableSubAccountNamesSet);
      if (subAccount) {
        result = subAccount;
        break;
      }
    }
    return result;
  }, [
    currentTabDefaultAccountId,
    clickToTradeDefaultSubaccount,
    globalDefaultSubAccountId,
    subAccountsByID,
    subAccountsByName,
    tradableSubAccountNamesSet,
  ]);

  return {
    defaultSubAccount,
    defaultClickToTradeSubAccount,
  };
}

function getSubAccount(
  subAccountID: number | undefined,
  subAccountsByID: Map<number, SubAccount>,
  tradableSubAccountNamesSet: Set<string>
): SubAccount | undefined {
  if (!subAccountID) {
    return undefined;
  }
  const defaultSubAccount = subAccountsByID.get(subAccountID);
  if (!defaultSubAccount) {
    return undefined;
  }
  if (tradableSubAccountNamesSet.has(defaultSubAccount.Name)) {
    return defaultSubAccount;
  }
  return undefined;
}
